import { Label } from '@/components/ui-kit/label';
import { getNestedValue, getSuggestionPlaceholder, resolveTemplateVariables } from '../utils';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import AISuggestionIndicator from './AISuggestionIndicator';
import { AISuggestionStatusBadge } from './AISuggestionStatusBadge';
import { FieldRendererProps } from '../types';

/**
 * Email Field Renderer
 */
export function EmailField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;

  // Resolve template variables
  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);
  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Use AI suggestion as placeholder if available
  const suggestionPlaceholder = showSuggestion
    ? getSuggestionPlaceholder(
        field.aiSuggestionKey,
        enrichmentContext,
        field.placeholder || 'email@example.com'
      )
    : resolveTemplateVariables(field.placeholder, formData, enrichmentContext) ||
      'email@example.com';

  // Check if suggestion is available and field is empty (for layout adjustments)
  const currentStr = value !== undefined && value !== null ? String(value) : '';
  const hasEnrichment = enrichmentContext && Object.keys(enrichmentContext).length > 0;
  const suggestedValue =
    showSuggestion && hasEnrichment
      ? getNestedValue(enrichmentContext, field.aiSuggestionKey as string)
      : null;
  const showInlineButton =
    showSuggestion && hasEnrichment && suggestedValue !== null && currentStr.length === 0;

  return (
    <div className="space-y-2">
      <div className="flex  justify-between gap-4  items-start mb-2">
        <div>
          <div className="flex items-start justify-between gap-2">
            <Label
              htmlFor={field.name}
              className="text-base font-semibold font-nunito text-primary-light"
            >
              {resolvedLabel}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {showSuggestion && (
              <AISuggestionStatusBadge
                suggestionKey={field.aiSuggestionKey as string}
                enrichmentContext={enrichmentContext}
                currentValue={value}
                className=" md:hidden mt-1"
              />
            )}
          </div>

          {resolvedHelpText && <p className="text-sm text-secondary-300">{resolvedHelpText}</p>}
        </div>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey as string}
            enrichmentContext={enrichmentContext}
            currentValue={value}
            className="hidden md:flex"
          />
        )}
      </div>
      <div className="relative">
        <Input
          id={field.name}
          type="email"
          placeholder={suggestionPlaceholder}
          value={(value as string) || ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          className={cn(
            error ? 'border-destructive' : '',
            showInlineButton ? 'sm:pr-44' : '',
            'shadow-none border border-secondary-100'
          )}
        />
        {showSuggestion && (
          <>
            {/* Desktop: Inside input */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:block">
              <AISuggestionIndicator
                suggestionKey={field.aiSuggestionKey as string}
                enrichmentContext={enrichmentContext}
                currentValue={value}
                onApply={(suggestedValue) => onChange(field.name, suggestedValue)}
                variant="inline"
              />
            </div>
            {/* Mobile: Below input */}
            <div className="mt-2 sm:hidden">
              <AISuggestionIndicator
                suggestionKey={field.aiSuggestionKey as string}
                enrichmentContext={enrichmentContext}
                currentValue={value}
                onApply={(suggestedValue) => onChange(field.name, suggestedValue)}
                variant="inline"
              />
            </div>
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
